import React, { FC, useEffect, useState } from "react"
import { Form, InputNumber, Select, Modal } from "antd"
import { observer } from "mobx-react"
import { notifications } from "notifications"
import { SubmitCancelButtons } from "components"
import { useStore } from "stores/utils/use-store"
import { CountryMultiplierDto } from "../store/types"
import { getCountrySelectOptions, defaultFilterOption } from "../utils"
import { getGameOptionsWithNumberValues } from "components/gameSelector/gameOptions"

const { Option } = Select

interface CreateEditCountryMultiplierModalProps {
  visible: boolean
  onClose: () => void
  initialData?: CountryMultiplierDto | null
}

export const CreateEditCountryMultiplierModal: FC<CreateEditCountryMultiplierModalProps> = observer(({
  visible,
  onClose,
  initialData,
}) => {
  const { dataStore: { countryGroupsStore } } = useStore()
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)
  const [scopeType, setScopeType] = useState<"country" | "group">("country")

  const gameOptions = getGameOptionsWithNumberValues()
  const countryOptions = getCountrySelectOptions()

  const groupOptions = countryGroupsStore.countryGroups.map(group => ({
    label: group.name,
    value: group.id,
  }))

  useEffect(() => {
    if (visible) {
      if (initialData) {
        let type: "country" | "group" = "country"
        if (initialData.countryGroupId) {
          type = "group"
        } else if (initialData.countryCode) {
          type = "country"
        }

        setScopeType(type)
        form.setFieldsValue({
          gameId: initialData.gameId,
          countryCode: initialData.countryCode,
          countryGroupId: initialData.countryGroupId,
          multiplier: initialData.multiplier,
        })
      } else {
        setScopeType("country")
        form.resetFields()
      }
    }
  }, [visible, initialData, form])

  const onFinishHandler = async(values: any) => {
    if (submitting) return

    const data: any = { multiplier: values.multiplier }

    if (scopeType === "country") {
      data.gameId = values.gameId || null
      data.countryCode = values.countryCode
      data.countryGroupId = null
    } else if (scopeType === "group") {
      data.gameId = values.gameId || null
      data.countryCode = null
      data.countryGroupId = values.countryGroupId
    }

    setSubmitting(true)

    try {
      if (initialData) {
        await countryGroupsStore.updateCountryMultiplier(initialData.id, data)
        notifications.success("Country multiplier updated")
      } else {
        await countryGroupsStore.createCountryMultiplier(data)
        notifications.success("Country multiplier created")
      }
      setSubmitting(false)
      onClose()
      form.resetFields()
    } catch (error: any) {
      notifications.error(error?.cause?.message || error?.message || "Failed to save country multiplier")
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    form.resetFields()
    onClose()
  }

  return (
    <Modal
      open={visible}
      title={initialData ? "Edit Country Multiplier" : "Create Country Multiplier"}
      width="700px"
      footer={null}
      onCancel={handleClose}
      style={{ top: "10px" }}
    >
      <Form
        form={form}
        onFinish={onFinishHandler}
        layout="vertical"
        id="createEditCountryMultiplier"
      >
        <Form.Item
          label="Scope Type"
          name="scopeType"
          initialValue="country"
        >
          <Select
            value={scopeType}
            onChange={value => {
              setScopeType(value)
              form.setFieldsValue({
                gameId: undefined,
                countryCode: undefined,
                countryGroupId: undefined,
              })
            }}
          >
            <Option value="country">By Country</Option>
            <Option value="group">By Country Group</Option>
          </Select>
        </Form.Item>

        {scopeType === "country" && (
          <>
            <Form.Item
              label="Game ID (Optional)"
              name="gameId"
            >
              <Select
                placeholder="Select a game (optional)"
                allowClear
                showSearch
                filterOption={defaultFilterOption}
              >
                {gameOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Country"
              name="countryCode"
              rules={[
                {
                  required: true,
                  message: "Please select a country",
                },
              ]}
            >
              <Select
                placeholder="Select a country"
                showSearch
                filterOption={defaultFilterOption}
              >
                {countryOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </>
        )}

        {scopeType === "group" && (
          <>
            <Form.Item
              label="Game ID (Optional)"
              name="gameId"
            >
              <Select
                placeholder="Select a game (optional)"
                allowClear
                showSearch
                filterOption={defaultFilterOption}
              >
                {gameOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Country Group"
              name="countryGroupId"
              rules={[
                {
                  required: true,
                  message: "Please select a country group",
                },
              ]}
            >
              <Select
                placeholder="Select a country group"
                showSearch
                filterOption={defaultFilterOption}
              >
                {groupOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </>
        )}

        <Form.Item
          label="Multiplier"
          name="multiplier"
          rules={[
            {
              required: true,
              message: "Please enter a multiplier",
            },
            {
              type: "number",
              min: 0.1,
              message: "Multiplier must be greater than 0",
            },
          ]}
        >
          <InputNumber style={{ width: "100%" }} min={0.1} step={0.1} placeholder="Enter multiplier value" />
        </Form.Item>

        <SubmitCancelButtons
          formId="createEditCountryMultiplier"
          onClose={handleClose}
          isLoading={submitting || countryGroupsStore.isLoading}
        />
      </Form>
    </Modal>
  )
})
